import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import {
  addDays,
  orderToPay,
  verifyPayments,
} from '@actual-app/core/shared/mtxbudget';

import { NotConfigured } from './QuinzainePage';
import { MtxPage, Section, shortDate, textDate, useMoney } from './ui';
import { useMtxbudget } from './useMtxbudget';
import { useTransactionsSince } from './useTransactions';

export function PayerPage() {
  const { t } = useTranslation();
  const money = useMoney();
  const { config, saveConfig, plan, toPay, today, isLoading } = useMtxbudget();
  const movements = useTransactionsSince(addDays(today, -40));

  const next = plan[0];
  if (!config || !next) {
    return (
      <MtxPage title={t('À payer')} back="/quinzaine">
        {!isLoading && <NotConfigured />}
      </MtxPage>
    );
  }

  const ordered = orderToPay(toPay, today);
  // What the bank shows, whatever was ticked here.
  const proofs = verifyPayments(
    ordered,
    movements.map(m => ({
      id: m.id,
      date: m.date,
      amount: m.amount,
      account: m.account,
      scheduleId: m.schedule,
    })),
  );

  function toggle(id: string) {
    if (!config) {
      return;
    }
    const paid = { ...config.paid };
    if (paid[id]) {
      delete paid[id];
    } else {
      paid[id] = today;
    }
    saveConfig({ ...config, paid });
  }

  const rows = ordered.map((charge, index) => {
    const proof = proofs[charge.id];
    const ticked = Boolean(config.paid[charge.id]);
    const late = charge.date < today;
    const status = proof
      ? {
          text: t('Vérifié à la banque : {{amount}} le {{date}}', {
            amount: money(-proof.amount),
            date: textDate(proof.date),
          }),
          color: theme.noticeTextLight,
        }
      : ticked
        ? {
            text: t('Coché, pas encore vu à la banque'),
            color: theme.warningText,
          }
        : late
          ? { text: t('En retard'), color: theme.errorText }
          : { text: t('À payer'), color: theme.pageTextSubdued };
    return { charge, rank: index + 1, proof, ticked, status };
  });
  const done = rows.filter(row => row.proof || row.ticked);
  const total = rows.reduce((sum, row) => sum - row.charge.amount, 0);
  const paid = done.reduce((sum, row) => sum - row.charge.amount, 0);

  return (
    <MtxPage title={t('À payer')} back="/quinzaine">
      <View style={{ gap: 12, paddingBottom: 20 }}>
        <Section style={{ padding: 16, gap: 4 }}>
          <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
            {t("Avec la paie du {{date}}, jusqu'au {{end}}", {
              date: textDate(next.payday),
              end: textDate(addDays(next.nextPayday, -1)),
            })}
          </Text>
          <Text style={{ fontSize: 26, fontWeight: 600 }}>
            {t('Reste {{amount}}', { amount: money(total - paid) })}
          </Text>
          <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
            {t(
              '{{done}} paiement(s) faits sur {{count}} · {{paid}} sur {{total}}',
              {
                done: done.length,
                count: rows.length,
                paid: money(paid),
                total: money(total),
              },
            )}
          </Text>
        </Section>

        <Section
          title={t('Par ordre de priorité')}
          aside={t("en retard d'abord, puis par date")}
        >
          {rows.length === 0 && (
            <Text style={{ padding: 16, color: theme.pageTextSubdued }}>
              {t('Rien à payer sur cette période.')}
            </Text>
          )}
          {rows.map(({ charge, rank, proof, ticked, status }, index) => (
            <label
              key={charge.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 16px',
                minHeight: 44,
                borderBottom:
                  index === rows.length - 1
                    ? undefined
                    : `1px solid ${theme.tableBorder}`,
              }}
            >
              <input
                type="checkbox"
                checked={ticked || Boolean(proof)}
                disabled={Boolean(proof)}
                onChange={() => toggle(charge.id)}
                style={{ width: 22, height: 22, flexShrink: 0 }}
              />
              <Text
                style={{
                  width: 20,
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: 600,
                  color: theme.pageTextSubdued,
                }}
              >
                {rank}
              </Text>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={{ fontWeight: 500 }}>{charge.name}</Text>
                <Text style={{ fontSize: 12, color: status.color }}>
                  {shortDate(charge.date)} · {status.text}
                </Text>
              </View>
              <Text style={{ fontWeight: 500, flexShrink: 0 }}>
                {money(-charge.amount)}
              </Text>
            </label>
          ))}
        </Section>

        <Text
          style={{
            fontSize: 12,
            color: theme.pageTextSubdued,
            padding: '0 4px',
          }}
        >
          {t(
            "Un paiement est « vérifié » quand une transaction du même compte et du même montant apparaît à la banque dans les jours qui entourent l'échéance. Une case cochée sans transaction reste signalée.",
          )}
        </Text>
      </View>
    </MtxPage>
  );
}
